import { store } from '../state.js';
import { formatMoney, formatNumber, daysAgo, startOfDay } from '../utils.js';
import { DIRECTIONS_BY_ID } from '../directionsData.js';
import { CITIES } from '../data.js';
import { renderLeadPanel } from '../leadPanel.js';

let period = '7';
let distributionMode = 'services';

function leadsInPeriod(state, days) {
    if (days === 'today') {
        const start = startOfDay(Date.now());
        return state.leads.filter(l => l.createdAt >= start);
    }
    const cutoff = daysAgo(Number(days));
    return state.leads.filter(l => l.createdAt >= cutoff);
}

function previousPeriodLeads(state, days) {
    if (days === 'today') {
        const start = startOfDay(Date.now());
        const prevStart = start - 24 * 60 * 60 * 1000;
        return state.leads.filter(l => l.createdAt >= prevStart && l.createdAt < start);
    }
    const n = Number(days);
    const cutoffNow = daysAgo(n);
    const cutoffPrev = daysAgo(n * 2);
    return state.leads.filter(l => l.createdAt >= cutoffPrev && l.createdAt < cutoffNow);
}

function pctChange(current, prev) {
    if (prev === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - prev) / prev) * 100);
}

function conversion(leads) {
    if (!leads.length) return 0;
    return (leads.filter(l => l.status === 'deal').length / leads.length) * 100;
}

function buildChart(state, leads, days) {
    const byHour = days === 'today';
    const buckets = new Map();

    if (byHour) {
        for (let h = 8; h <= 22; h++) buckets.set(h, { leads: 0, deals: 0 });
        leads.forEach(l => {
            const h = new Date(l.createdAt).getHours();
            if (buckets.has(h)) {
                buckets.get(h).leads++;
                if (l.status === 'deal') buckets.get(h).deals++;
            }
        });
    } else {
        const n = Number(days);
        for (let i = n - 1; i >= 0; i--) {
            const d = new Date(startOfDay(Date.now()) - i * 86400000);
            buckets.set(d.getDate(), { leads: 0, deals: 0, date: d });
        }
        leads.forEach(l => {
            const key = new Date(startOfDay(l.createdAt)).getDate();
            if (buckets.has(key)) {
                buckets.get(key).leads++;
                if (l.status === 'deal') buckets.get(key).deals++;
            }
        });
    }

    const entries = [...buckets.entries()];
    const max = Math.max(1, ...entries.map(([, v]) => v.leads));

    const bars = entries.map(([key, v]) => {
        const leadsH = Math.round((v.leads / max) * 100);
        const dealsH = v.leads ? Math.round((v.deals / v.leads) * leadsH) : 0;
        const label = byHour ? `${key}:00` : key;
        return `
            <div class="ov-chart__col" title="${v.leads} заявок">
                <div class="ov-chart__bar" style="height:${leadsH}%">
                    <div class="ov-chart__bar-deals" style="height:${dealsH}%"></div>
                </div>
                <div class="ov-chart__label">${label}</div>
            </div>
        `;
    }).join('');

    return `<div class="ov-chart">${bars}</div>`;
}

function buildDistribution(state, leads) {
    const counts = new Map();

    if (distributionMode === 'services') {
        leads.forEach(l => {
            const dir = DIRECTIONS_BY_ID[l.directionId];
            const label = dir ? dir.title : l.directionId;
            counts.set(label, (counts.get(label) || 0) + 1);
        });
    } else {
        leads.forEach(l => {
            const city = CITIES.find(c => c.id === l.cityId);
            const label = city ? city.name : l.cityId;
            counts.set(label, (counts.get(label) || 0) + 1);
        });
    }

    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
    const max = Math.max(1, ...sorted.map(e => e[1]));

    return sorted.map(([label, count]) => `
        <div class="ov-dist__row">
            <div class="ov-dist__label">${label}</div>
            <div class="ov-dist__bar-wrap">
                <div class="ov-dist__bar" style="width:${Math.round((count / max) * 100)}%"></div>
            </div>
            <div class="ov-dist__count">${count}</div>
        </div>
    `).join('') || '<div class="ov-dist__empty">Пока нет данных</div>';
}

export function renderOverview(container) {
    const state = store.get();
    const leads = leadsInPeriod(state, period);
    const prevLeads = previousPeriodLeads(state, period);

    const avgCost = leads.length
        ? leads.reduce((sum, l) => sum + l.cost, 0) / leads.length
        : 0;
    const prevAvgCost = prevLeads.length
        ? prevLeads.reduce((sum, l) => sum + l.cost, 0) / prevLeads.length
        : 0;

    const conv = conversion(leads);
    const prevConv = conversion(prevLeads);
    const convDiff = Math.round((conv - prevConv) * 10) / 10;

    const leadsChange = pctChange(leads.length, prevLeads.length);

    const avgSpendPerDay = state.leads
        .filter(l => l.createdAt >= daysAgo(7))
        .reduce((sum, l) => sum + l.cost, 0) / 7;
    const enoughFor = avgSpendPerDay > 0 ? Math.round(state.balance / (avgSpendPerDay / 7)) : 0;

    const recent = state.leads.slice(0, 7);

    const newCount = state.leads.filter(l => l.status === 'new').length;
    const reviewCount = state.leads.filter(l => l.dispute && l.dispute.status === 'review').length;
    const activeDirCount = store.activeDirectionIds().length;
    const connectedCities = store.connectedCitiesCount();

    container.innerHTML = `
        <div class="ov-period">
            <button type="button" class="ov-period__btn${period === 'today' ? ' active' : ''}" data-period="today">Сегодня</button>
            <button type="button" class="ov-period__btn${period === '7' ? ' active' : ''}" data-period="7">7 дней</button>
            <button type="button" class="ov-period__btn${period === '30' ? ' active' : ''}" data-period="30">30 дней</button>
        </div>

        <div class="ov-stats">
            <div class="ov-stat">
                <div class="ov-stat__label">Заявок</div>
                <div class="ov-stat__val">${leads.length}</div>
                <div class="ov-stat__change ${leadsChange >= 0 ? 'positive' : 'negative'}">${leadsChange >= 0 ? '↑' : '↓'} ${Math.abs(leadsChange)}% к периоду до</div>
            </div>
            <div class="ov-stat">
                <div class="ov-stat__label">Средняя цена заявки</div>
                <div class="ov-stat__val">${formatMoney(avgCost)}</div>
                <div class="ov-stat__sub">без учёта возвратов</div>
            </div>
            <div class="ov-stat">
                <div class="ov-stat__label">Конверсия в сделку</div>
                <div class="ov-stat__val">${conv.toFixed(0)}%</div>
                <div class="ov-stat__change ${convDiff >= 0 ? 'positive' : 'negative'}">${convDiff >= 0 ? '↑' : '↓'} ${Math.abs(convDiff)} п.п.</div>
            </div>
            <div class="ov-stat ov-stat--accent">
                <div class="ov-stat__label">Баланс</div>
                <div class="ov-stat__val">${formatMoney(state.balance)}</div>
                <div class="ov-stat__sub">хватит примерно на ${formatNumber(Math.max(enoughFor, 0))} заявок</div>
            </div>
        </div>

        <div class="ov-grid">
            <div class="ov-card">
                <div class="ov-card__title">Заявки по ${period === 'today' ? 'часам' : 'дням'}</div>
                ${buildChart(state, leads, period)}
                <div class="ov-chart__legend">
                    <span><i class="ov-chart__dot ov-chart__dot--leads"></i>Заявки</span>
                    <span><i class="ov-chart__dot ov-chart__dot--deals"></i>Сделки</span>
                </div>
            </div>
            <div class="ov-card">
                <div class="ov-card__head">
                    <div class="ov-card__title">Распределение</div>
                    <div class="ov-card__switch">
                        <button type="button" class="ov-card__switch-btn${distributionMode === 'services' ? ' active' : ''}" data-dist="services">Услуги</button>
                        <button type="button" class="ov-card__switch-btn${distributionMode === 'cities' ? ' active' : ''}" data-dist="cities">Города</button>
                    </div>
                </div>
                <div class="ov-dist">${buildDistribution(state, leads)}</div>
            </div>
        </div>

        <div class="ov-card">
            <div class="ov-card__title">Последние заявки</div>
            <div class="ov-recent">
                ${recent.map(l => `
                    <button type="button" class="ov-recent__item" data-lead-id="${l.id}">
                        <div>
                            <div class="ov-recent__title">${DIRECTIONS_BY_ID[l.directionId] ? DIRECTIONS_BY_ID[l.directionId].title : l.directionId}</div>
                            <div class="ov-recent__meta">№${l.number} · ${l.name}</div>
                        </div>
                        <span class="ov-recent__status ov-recent__status--${l.status}">${statusLabel(l)}</span>
                    </button>
                `).join('')}
            </div>
        </div>

        <div class="ov-card ov-summary">
            <div class="ov-card__title">Сводка «Сегодня»</div>
            <div class="ov-summary__grid">
                <div class="ov-summary__item"><span>Поступило из лимита</span><b>${state.todayCount} / ${state.dailyLimit}</b></div>
                <div class="ov-summary__item"><span>Ждут звонка</span><b>${newCount}</b></div>
                <div class="ov-summary__item"><span>На проверке</span><b>${reviewCount}</b></div>
                <div class="ov-summary__item"><span>Расписание</span><b>${state.schedule.roundTheClock ? 'Круглосуточно' : state.schedule.from + '–' + state.schedule.to}</b></div>
                <div class="ov-summary__item"><span>Активные направления</span><b>${activeDirCount}</b></div>
                <div class="ov-summary__item"><span>Городов в работе</span><b>${connectedCities} из 11</b></div>
            </div>
            <div class="ov-summary__hint">Отвечаем на заявки клиентов в течение 15 минут в рабочее время.</div>
        </div>
    `;

    container.querySelectorAll('.ov-period__btn').forEach(btn => {
        btn.addEventListener('click', () => {
            period = btn.dataset.period;
            renderOverview(container);
        });
    });

    container.querySelectorAll('.ov-card__switch-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            distributionMode = btn.dataset.dist;
            renderOverview(container);
        });
    });

    container.querySelectorAll('.ov-recent__item').forEach(btn => {
        btn.addEventListener('click', () => renderLeadPanel(btn.dataset.leadId));
    });
}

function statusLabel(lead) {
    if (lead.dispute && lead.dispute.status === 'review') return 'На проверке';
    const map = { new: 'Новая', progress: 'В работе', called: 'Дозвонились', deal: 'Сделка', declined: 'Отказ' };
    return map[lead.status] || lead.status;
}
